import axios from "axios";

export enum SettingKind {
    Text = "Text",
    Number = "Number",
    Bytes = "Bytes",
    Boolean = "Boolean",
    Secret = "Secret",
    Date = "Date",
}

export type SystemSetting = {
    key: string;
    name: string;
    group: string;
    kind: SettingKind;
    hint: string | null;
    /** Always null for secrets; they are never sent back to the browser. */
    value: string | null;
    isSet: boolean;
    isSecret: boolean;
    /** Made up by the server; the ACP offers Generate instead of a text field. */
    generated: boolean;
    /** Read-only, e.g. the redirect URI to register with a sign-in provider. */
    reference: SystemSettingReference | null;
};

export type SystemSettingReference = {
    label: string;
    value: string;
};

export type SystemSettings = {
    groups: string[];
    settings: SystemSetting[];
};

export type SystemSettingChange = {
    key: string;
    value: string | null;
    clear: boolean;
};

export type SettingsTestResult = {
    success: boolean;
    message: string;
};

export type SystemSettingsApi = {
    get(): Promise<SystemSettings>;
    save(changes: SystemSettingChange[]): Promise<void>;
    /** Returns the new value, which the server never sends again. */
    generate(key: string): Promise<string>;
    testEmail(toAddress: string): Promise<SettingsTestResult>;
    testInbound(): Promise<SettingsTestResult>;
    testStorage(): Promise<SettingsTestResult>;
    testConverter(): Promise<SettingsTestResult>;
};

export const systemSettingsApi: SystemSettingsApi = {
    async get() {
        const { data } = await axios.get<SystemSettings>("/api/v0/system-settings");
        return data;
    },
    async save(changes) {
        await axios.put("/api/v0/system-settings", { changes });
    },
    async generate(key) {
        const { data } = await axios.post<{ value: string }>("/api/v0/system-settings/generate", {
            key,
        });
        return data.value;
    },
    async testEmail(toAddress) {
        const { data } = await axios.post<SettingsTestResult>(
            "/api/v0/system-settings/test/email",
            {
                toAddress,
            }
        );
        return data;
    },
    async testInbound() {
        const { data } = await axios.post<SettingsTestResult>(
            "/api/v0/system-settings/test/inbound"
        );
        return data;
    },
    async testStorage() {
        const { data } = await axios.post<SettingsTestResult>(
            "/api/v0/system-settings/test/storage"
        );
        return data;
    },
    async testConverter() {
        const { data } = await axios.post<SettingsTestResult>(
            "/api/v0/system-settings/test/converter"
        );
        return data;
    },
};
